import React from 'react';
import { View, Text, ViewStyle, TextStyle } from 'react-native';
import { useTheme } from '../context/themeContext';

export type ItemState = 'DRAFT' | 'PUBLISHED' | 'DEACTIVATED' | 'ARCHIVED' | 'SCHEDULED';
export type StateBadgeSize = 'default' | 'lg';

interface StateBadgeProps {
  state: ItemState;
  size?: StateBadgeSize;
  style?: ViewStyle;
}

/**
 * SKCATALOG-CC-002 CR-6/CR-7 · SKCATALOG-DB-001 §4 — FORM-coded lifecycle chips.
 *
 * The states used to be distinguished by colour alone. Colour alone is not a
 * distinction: roughly one man in twelve cannot separate the success green from
 * the danger red, and this particular pair is the one that matters most — a
 * DEACTIVATED item is not sellable and a PUBLISHED one is, and mistaking them
 * means quoting a price for a service that has been withdrawn.
 *
 * So each state now also differs in FORM, and the form carries the meaning:
 *
 *   Draft       outline  — an empty outline reads as "not yet a thing".
 *   Published   fill     — solid, the only sellable state; the filled chip is the
 *                          heaviest mark in the row, which is what you want to be
 *                          able to spot at a glance down a list.
 *   Deactivated dashed   — a broken border reads as interrupted/withdrawn.
 *   Archived    muted    — low contrast, no dot; it has left the working catalog.
 *   Scheduled   outline + dotted dot — live later, not live now.
 *
 * Colour is kept as a redundant cue, not the only one.
 */
interface StateMeta {
  label: string;
  tone: 'neutral' | 'success' | 'danger' | 'outline' | 'info';
  /** The form-coding — see the note above. */
  form: 'outline' | 'fill' | 'dashed' | 'muted';
  /** Whether the leading dot is drawn (Archived has none — nothing is live). */
  dot: boolean;
}

const STATE_META: Record<ItemState, StateMeta> = {
  DRAFT:       { label: 'Draft',       tone: 'neutral', form: 'outline', dot: true  },
  PUBLISHED:   { label: 'Published',   tone: 'success', form: 'fill',    dot: true  },
  DEACTIVATED: { label: 'Deactivated', tone: 'danger',  form: 'dashed',  dot: true  },
  ARCHIVED:    { label: 'Archived',    tone: 'outline', form: 'muted',   dot: false },
  SCHEDULED:   { label: 'Scheduled',   tone: 'info',    form: 'outline', dot: true  },
};

/** Is this state sellable? Mirrors CR-6's gate — PUBLISHED only. Exported so a
 *  caller can label a chip's surroundings ("not sellable") consistently. */
export function isSellableState(state: ItemState): boolean {
  return state === 'PUBLISHED';
}

export default function StateBadge({ state, size = 'default', style }: StateBadgeProps) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii } = theme;

  const meta = STATE_META[state] ?? STATE_META.DRAFT;

  type ToneColors = { bg: string; border: string; color: string; dotColor: string };

  const toneMap: Record<string, ToneColors> = {
    info:    { bg: colors.infoSoft,    border: colors.infoLine,    color: colors.info,    dotColor: colors.info },
    success: { bg: colors.successSoft, border: colors.successLine, color: colors.success, dotColor: colors.success },
    warn:    { bg: colors.warnSoft,    border: colors.warnLine,    color: colors.warn,    dotColor: colors.warn },
    danger:  { bg: colors.dangerSoft,  border: colors.dangerLine,  color: colors.danger,  dotColor: colors.danger },
    neutral: { bg: colors.neutralSoft, border: colors.neutralLine, color: colors.neutral, dotColor: colors.neutral },
    outline: { bg: colors.transparent, border: colors.line,        color: colors.ink2,    dotColor: colors.ink4 },
  };

  const tc: ToneColors = toneMap[meta.tone] ?? toneMap.neutral;

  const height = size === 'lg' ? 26 : 22;
  const hPad = size === 'lg' ? 12 : 9;
  const fSize = size === 'lg' ? fontSize.sm : fontSize.xs;

  // Form-coding. `fill` inverts (solid ground, light ink) so PUBLISHED is the
  // heaviest mark in a list; `dashed` breaks the border; `muted` drops contrast
  // and the border weight; `outline` is the transparent baseline.
  const isFill = meta.form === 'fill';
  const isMuted = meta.form === 'muted';

  const containerStyle: ViewStyle = {
    height,
    paddingHorizontal: hPad,
    borderRadius: radii.full,
    borderWidth: 1,
    borderStyle: meta.form === 'dashed' ? 'dashed' : 'solid',
    borderColor: isFill ? tc.color : isMuted ? colors.line : tc.border,
    backgroundColor: isFill ? tc.color : isMuted ? colors.transparent : tc.bg,
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 5,
    ...(isMuted ? { opacity: 0.72 } : {}),
  };

  const labelStyle: TextStyle = {
    fontSize: fSize,
    // The filled chip needs the heavier weight to stay legible on its own ground.
    fontWeight: isFill ? fontWeight.semibold : fontWeight.medium,
    color: isFill ? colors.white : isMuted ? colors.ink4 : tc.color,
    fontFamily: theme.font.sans,
    includeFontPadding: false,
  };

  return (
    <View
      style={[containerStyle, style]}
      // Screen readers get the sellability, which is the fact the form-coding
      // encodes visually — otherwise the shape distinction is sighted-only.
      accessibilityRole="text"
      accessibilityLabel={`${meta.label} — ${isSellableState(state) ? 'sellable' : 'not sellable'}`}
    >
      {meta.dot && (
        <View
          style={{
            width: 7,
            height: 7,
            borderRadius: 3.5,
            backgroundColor: isFill ? colors.white : tc.dotColor,
          }}
        />
      )}
      <Text style={labelStyle}>{meta.label}</Text>
    </View>
  );
}
